import { baseApi as api } from "./baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getOperatorOpenApiyaml: build.query<
      GetOperatorOpenApiyamlApiResponse,
      GetOperatorOpenApiyamlApiArg
    >({
      query: () => ({ url: `/v1/operator/openapi.yaml` }),
    }),
    listOperatorTenants: build.query<
      ListOperatorTenantsApiResponse,
      ListOperatorTenantsApiArg
    >({
      query: () => ({ url: `/v1/operator/tenants` }),
    }),
    createTenant: build.mutation<CreateTenantApiResponse, CreateTenantApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants`,
        method: "POST",
        body: queryArg.createTenant,
      }),
    }),
    getOperatorTenant: build.query<
      GetOperatorTenantApiResponse,
      GetOperatorTenantApiArg
    >({
      query: (queryArg) => ({ url: `/v1/operator/tenants/${queryArg.tenant}` }),
    }),
    updateTenant: build.mutation<UpdateTenantApiResponse, UpdateTenantApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}`,
        method: "PATCH",
        body: queryArg.tenantDetails,
      }),
    }),
    offboardTenant: build.mutation<
      OffboardTenantApiResponse,
      OffboardTenantApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/offboard`,
        method: "POST",
      }),
    }),
    retryPurge: build.mutation<RetryPurgeApiResponse, RetryPurgeApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/purge`,
        method: "POST",
      }),
    }),
    reinstateTenant: build.mutation<
      ReinstateTenantApiResponse,
      ReinstateTenantApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/reinstate`,
        method: "POST",
      }),
    }),
    listAdministrators: build.query<
      ListAdministratorsApiResponse,
      ListAdministratorsApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/administrators`,
      }),
    }),
    addAdministrator: build.mutation<
      AddAdministratorApiResponse,
      AddAdministratorApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/administrators`,
        method: "POST",
        body: queryArg.addAdministrator,
      }),
    }),
    removeAdministrator: build.mutation<
      RemoveAdministratorApiResponse,
      RemoveAdministratorApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/administrators/${queryArg.subject}`,
        method: "DELETE",
      }),
    }),
    resetAdministratorMfa: build.mutation<
      ResetAdministratorMfaApiResponse,
      ResetAdministratorMfaApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/administrators/${queryArg.subject}/mfa/reset`,
        method: "POST",
      }),
    }),
    listDomains: build.query<ListDomainsApiResponse, ListDomainsApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/domains`,
      }),
    }),
    grantDomain: build.mutation<GrantDomainApiResponse, GrantDomainApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/domains`,
        method: "POST",
        body: queryArg.grantDomain,
      }),
    }),
    revokeDomain: build.mutation<RevokeDomainApiResponse, RevokeDomainApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/domains/${queryArg.host}`,
        method: "DELETE",
      }),
    }),
    listTenantTraffic: build.query<
      ListTenantTrafficApiResponse,
      ListTenantTrafficApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/traffic`,
      }),
    }),
    bindTenantTraffic: build.mutation<
      BindTenantTrafficApiResponse,
      BindTenantTrafficApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/traffic/${queryArg.website}`,
        method: "PUT",
        body: queryArg.bindTraffic,
      }),
    }),
    unbindTenantTraffic: build.mutation<
      UnbindTenantTrafficApiResponse,
      UnbindTenantTrafficApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/traffic/${queryArg.website}`,
        method: "DELETE",
        params: {
          expectedRevision: queryArg.expectedRevision,
        },
      }),
    }),
    collectTenantTraffic: build.mutation<
      CollectTenantTrafficApiResponse,
      CollectTenantTrafficApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/traffic/${queryArg.website}/runs`,
        method: "POST",
      }),
    }),
    getTenantBilling: build.query<
      GetTenantBillingApiResponse,
      GetTenantBillingApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing`,
      }),
    }),
    addAgreement: build.mutation<AddAgreementApiResponse, AddAgreementApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing/agreements`,
        method: "POST",
        body: queryArg.addAgreement,
      }),
    }),
    setTenantBilled: build.mutation<
      SetTenantBilledApiResponse,
      SetTenantBilledApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing/billed`,
        method: "PUT",
        body: queryArg.setBilled,
      }),
    }),
    recordPayment: build.mutation<
      RecordPaymentApiResponse,
      RecordPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing/payments`,
        method: "POST",
        body: queryArg.recordPayment,
      }),
    }),
    voidPayment: build.mutation<VoidPaymentApiResponse, VoidPaymentApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing/payments/${queryArg.entry}/void`,
        method: "POST",
        body: queryArg.voidEntry,
      }),
    }),
    addAdjustment: build.mutation<
      AddAdjustmentApiResponse,
      AddAdjustmentApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing/adjustments`,
        method: "POST",
        body: queryArg.addAdjustment,
      }),
    }),
    voidAdjustment: build.mutation<
      VoidAdjustmentApiResponse,
      VoidAdjustmentApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing/adjustments/${queryArg.entry}/void`,
        method: "POST",
        body: queryArg.voidEntry,
      }),
    }),
    setPeriodNote: build.mutation<
      SetPeriodNoteApiResponse,
      SetPeriodNoteApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/operator/tenants/${queryArg.tenant}/billing/notes/${queryArg.periodStart}`,
        method: "PUT",
        body: queryArg.periodNote,
      }),
    }),
    listPlans: build.query<ListPlansApiResponse, ListPlansApiArg>({
      query: () => ({ url: `/v1/operator/plans` }),
    }),
    createPlan: build.mutation<CreatePlanApiResponse, CreatePlanApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/plans`,
        method: "POST",
        body: queryArg.planInput,
      }),
    }),
    updatePlan: build.mutation<UpdatePlanApiResponse, UpdatePlanApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/plans/${queryArg.planId}`,
        method: "PUT",
        body: queryArg.planInput,
      }),
    }),
    listTrafficSources: build.query<
      ListTrafficSourcesApiResponse,
      ListTrafficSourcesApiArg
    >({
      query: () => ({ url: `/v1/operator/traffic/sources` }),
    }),
    listTrafficConnections: build.query<
      ListTrafficConnectionsApiResponse,
      ListTrafficConnectionsApiArg
    >({
      query: () => ({ url: `/v1/operator/traffic/connections` }),
    }),
    getBillingOverview: build.query<
      GetBillingOverviewApiResponse,
      GetBillingOverviewApiArg
    >({
      query: () => ({ url: `/v1/operator/billing` }),
    }),
    getLedger: build.query<GetLedgerApiResponse, GetLedgerApiArg>({
      query: (queryArg) => ({
        url: `/v1/operator/billing/ledger`,
        params: {
          from: queryArg["from"],
          to: queryArg.to,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as operatorApi };
export type GetOperatorOpenApiyamlApiResponse =
  /** status 200 The contract. */ Blob;
export type GetOperatorOpenApiyamlApiArg = void;
export type ListOperatorTenantsApiResponse =
  /** status 200 The tenants. */ TenantList;
export type ListOperatorTenantsApiArg = void;
export type CreateTenantApiResponse = /** status 201 The tenant. */ Tenant;
export type CreateTenantApiArg = {
  createTenant: CreateTenant;
};
export type GetOperatorTenantApiResponse = /** status 200 The tenant. */ Tenant;
export type GetOperatorTenantApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type UpdateTenantApiResponse = /** status 200 The tenant. */ Tenant;
export type UpdateTenantApiArg = {
  /** The tenant's id. */
  tenant: string;
  tenantDetails: TenantDetails;
};
export type OffboardTenantApiResponse =
  /** status 202 The tenant, now offboarding. */ Tenant;
export type OffboardTenantApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type RetryPurgeApiResponse = /** status 202 The tenant. */ Tenant;
export type RetryPurgeApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type ReinstateTenantApiResponse =
  /** status 200 The tenant, now active. */ Tenant;
export type ReinstateTenantApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type ListAdministratorsApiResponse =
  /** status 200 The administrators. */ Administrators;
export type ListAdministratorsApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type AddAdministratorApiResponse =
  /** status 201 The administrator. */ Administrator;
export type AddAdministratorApiArg = {
  /** The tenant's id. */
  tenant: string;
  addAdministrator: AddAdministrator;
};
export type RemoveAdministratorApiResponse = unknown;
export type RemoveAdministratorApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The administrator's subject, as `listAdministrators` returns it. */
  subject: string;
};
export type ResetAdministratorMfaApiResponse = unknown;
export type ResetAdministratorMfaApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The administrator's subject, as `listAdministrators` returns it. */
  subject: string;
};
export type ListDomainsApiResponse =
  /** status 200 The tenant's grants, ordered by host. */ DomainGrants;
export type ListDomainsApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type GrantDomainApiResponse = /** status 201 The grant. */ DomainGrant;
export type GrantDomainApiArg = {
  /** The tenant's id. */
  tenant: string;
  grantDomain: GrantDomain;
};
export type RevokeDomainApiResponse = unknown;
export type RevokeDomainApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The granted host, as `listDomains` returns it. */
  host: string;
};
export type ListTenantTrafficApiResponse =
  /** status 200 The tenant's sites, and its granted hosts no site answers on. */ TenantTraffic;
export type ListTenantTrafficApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type BindTenantTrafficApiResponse =
  /** status 200 The site, bound. */ TrafficSite;
export type BindTenantTrafficApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The site's id, as `listTenantTraffic` returns it. */
  website: string;
  bindTraffic: BindTraffic;
};
export type UnbindTenantTrafficApiResponse =
  /** status 200 The site, disconnected. */ TrafficSite;
export type UnbindTenantTrafficApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The site's id, as `listTenantTraffic` returns it. */
  website: string;
  /** The `revision` of the binding this disconnects, exactly as read.
    Any other binding is a 409. Absent, the binding as it stands.
     */
  expectedRevision?: string;
};
export type CollectTenantTrafficApiResponse =
  /** status 202 The site as read after the collection was scheduled, which may
already show it run. A client following the collection compares
`lastRunAt` with the value it displayed before the request.
 */ TrafficSite;
export type CollectTenantTrafficApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The site's id, as `listTenantTraffic` returns it. */
  website: string;
};
export type GetTenantBillingApiResponse =
  /** status 200 The account. */ Billing;
export type GetTenantBillingApiArg = {
  /** The tenant's id. */
  tenant: string;
};
export type AddAgreementApiResponse = /** status 201 The account. */ Billing;
export type AddAgreementApiArg = {
  /** The tenant's id. */
  tenant: string;
  addAgreement: AddAgreement;
};
export type SetTenantBilledApiResponse = /** status 200 The account. */ Billing;
export type SetTenantBilledApiArg = {
  /** The tenant's id. */
  tenant: string;
  setBilled: SetBilled;
};
export type RecordPaymentApiResponse = /** status 201 The account. */ Billing;
export type RecordPaymentApiArg = {
  /** The tenant's id. */
  tenant: string;
  recordPayment: RecordPayment;
};
export type VoidPaymentApiResponse = /** status 200 The account. */ Billing;
export type VoidPaymentApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The payment's or adjustment's id, as its statement line carries it. */
  entry: string;
  voidEntry: VoidEntry;
};
export type AddAdjustmentApiResponse = /** status 201 The account. */ Billing;
export type AddAdjustmentApiArg = {
  /** The tenant's id. */
  tenant: string;
  addAdjustment: AddAdjustment;
};
export type VoidAdjustmentApiResponse = /** status 200 The account. */ Billing;
export type VoidAdjustmentApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The payment's or adjustment's id, as its statement line carries it. */
  entry: string;
  voidEntry: VoidEntry;
};
export type SetPeriodNoteApiResponse = /** status 200 The account. */ Billing;
export type SetPeriodNoteApiArg = {
  /** The tenant's id. */
  tenant: string;
  /** The first day of the period the note is about. */
  periodStart: string;
  periodNote: PeriodNote;
};
export type ListPlansApiResponse = /** status 200 The plans. */ Plans;
export type ListPlansApiArg = void;
export type CreatePlanApiResponse = /** status 201 The plan. */ Plan;
export type CreatePlanApiArg = {
  planInput: PlanInput;
};
export type UpdatePlanApiResponse = /** status 200 The plan. */ Plan;
export type UpdatePlanApiArg = {
  /** The plan. */
  planId: string;
  planInput: PlanInput;
};
export type ListTrafficSourcesApiResponse =
  /** status 200 Every source, by kind. */ TrafficSources;
export type ListTrafficSourcesApiArg = void;
export type ListTrafficConnectionsApiResponse =
  /** status 200 Every connection, in the order declared. */ TrafficConnections;
export type ListTrafficConnectionsApiArg = void;
export type GetBillingOverviewApiResponse =
  /** status 200 The overview. */ BillingOverview;
export type GetBillingOverviewApiArg = void;
export type GetLedgerApiResponse =
  /** status 200 The lines, in date order. */ Ledger;
export type GetLedgerApiArg = {
  /** The first day included. */
  from: string;
  /** The last day included. */
  to: string;
};
export type Problem = {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
};
export type TenantStatus = "active" | "offboarding" | "offboarded";
export type Billed = boolean;
export type Interval = "monthly" | "annual";
export type Cents = number;
export type AccountSummary = {
  /** The plan of the agreement in force. Absent without one. */
  planName?: string;
  interval?: Interval;
  amount?: Cents;
  balance: Cents;
  overdue: Cents;
  oldestOverdue?: string;
  nextChargeOn?: string;
};
export type TenantSummary = {
  id: string;
  name: string;
  status: TenantStatus;
  billed: Billed;
  contactName?: string;
  contactEmail?: string;
  createdAt: string;
  offboardedAt?: string;
  account: AccountSummary;
};
export type TenantList = {
  tenants: TenantSummary[];
  /** Ids that hold administrators and have no registration. */
  unregistered: string[];
};
export type Actor = {
  subject: string;
  email?: string;
};
export type PurgeReport = {
  eventRows: number;
  readRows: number;
  domainRows: number;
  memberships: number;
  publicationOperations: number;
  webhookRows: number;
  /** The tenant's sites' daily visit counts and the records of how each site's are collected. */
  trafficRows: number;
  payloadObjects: number;
  blobs: number;
};
export type Purge = {
  report: PurgeReport;
  /** Why the latest attempt stopped. Absent once an attempt succeeds. */
  error?: string;
  attempts: number;
  updatedAt: string;
};
export type LogEntry = {
  at: string;
  actor: Actor;
  action: string;
  detail?: string;
};
export type Tenant = {
  id: string;
  name: string;
  status: TenantStatus;
  billed: Billed;
  contactName?: string;
  contactEmail?: string;
  notes?: string;
  createdAt: string;
  createdBy: Actor;
  offboardedAt?: string;
  offboardedBy?: Actor;
  purge?: Purge;
  /** When the tenant can be reinstated. Present only while it is
    `offboarded`; a reinstatement before this time is a 409.
     */
  reinstatableAt?: string;
  /** What operators did to this tenant, newest first. */
  log: LogEntry[];
};
export type AddAgreement = {
  planId: string;
  interval: Interval;
  /** The price per period. Defaults to the plan's price for the interval. */
  amount?: Cents;
  /** The first day charged. For a first agreement it defaults to
    today; for a change, to the next period boundary.
     */
  effectiveFrom?: string;
  /** Days after a period starts that its charge falls due. Defaults to 20. */
  paymentTermsDays?: number;
};
export type CreateTenant = {
  /** 3–40 lowercase letters, digits or inner hyphens. `account`,
    `auth` and `operator` are reserved. It cannot be changed afterwards.
     */
  id: string;
  name: string;
  contactName?: string;
  contactEmail?: string;
  notes?: string;
  /** `false` registers a tenant that is not charged. Such a tenant
    cannot have an agreement, so stating one as well is a 400.
     */
  billed?: boolean;
  agreement?: AddAgreement;
  /** The first administrator. Invited if they have no account. */
  administratorEmail?: string;
};
export type TenantDetails = {
  name: string;
  contactName?: string;
  contactEmail?: string;
  notes?: string;
};
export type Administrator = {
  subject: string;
  /** Absent when the account no longer exists. */
  email?: string;
  /** The name the account holder gave. Absent when they have set none. */
  name?: string;
  /** Whether the account signs in with two-step verification. False
    when the account no longer exists.
     */
  mfa: boolean;
};
export type Administrators = {
  administrators: Administrator[];
};
export type AddAdministrator = {
  email: string;
};
export type DomainBinding = {
  /** The site's tenant. Another tenant's only for a binding made before the host was granted. */
  tenant: string;
  website: string;
  /** The host is the site's main address, and its other hosts redirect to it. */
  canonical: boolean;
};
export type DomainGrant = {
  host: string;
  grantedAt: string;
  /** The subject of the operator who last granted it. */
  grantedBy: string;
  binding?: DomainBinding;
};
export type DomainGrants = {
  domains: DomainGrant[];
};
export type GrantDomain = {
  /** A bare hostname, with no scheme, port or path. */
  host: string;
};
export type TrafficHost = {
  host: string;
  /** The site's main address. */
  canonical: boolean;
  /** The host is granted to this tenant, so its visits are counted.
    A host the site answers on without a grant is not.
     */
  counted: boolean;
  /** The first day this host's visits are counted for the tenant
    under the site's binding. Absent for a host not counted, and
    for every host of a site with no binding.
     */
  countedFrom?: string;
};
export type TrafficStatus =
  | "pending"
  | "ok"
  | "failing"
  | "refused"
  | "window"
  | "noAddress"
  | "noConnection"
  | "notConfigured"
  | "unbound";
export type TrafficBinding = {
  /** The connection's `ref`. Absent once disconnected. */
  connection?: string;
  status: TrafficStatus;
  /** When the site was last bound. Disconnecting it does not change this. */
  boundAt: string;
  /** Changes on every bind and every disconnect. Opaque; send it back
    exactly as read, as `expectedRevision`, to bind or disconnect
    over this binding.
     */
  revision: string;
  /** The day the bind said the tenant held the site's web addresses from. */
  historyFrom?: string;
  /** When `historyFrom` was last stated. It covers the web addresses
    whose grant is dated to an earlier second, and no other.
    Present exactly when `historyFrom` is.
     */
  vouchedAt?: string;
  /** The first collection that failed since the last that succeeded. */
  failingSince?: string;
  /** The last collection that reached the site. */
  lastRunAt?: string;
  lastError?: string;
  /** The earliest day collected. */
  collectedFrom?: string;
  /** The latest day collected. */
  collectedThrough?: string;
};
export type TrafficSite = {
  website: string;
  /** The site's main address. For a site with no address left, the
    main address it had when it was bound.
     */
  label: string;
  /** Every host the site answers on, main address first. */
  hosts: TrafficHost[];
  binding?: TrafficBinding;
};
export type TenantTraffic = {
  sites: TrafficSite[];
  /** Hosts granted to the tenant that no site answers on, so nothing counts them. */
  unassignedHosts: string[];
};
export type BindTraffic = {
  /** A connection's `ref`, as `listTrafficConnections` lists it. */
  connection: string;
  /** Checked against the connection's source's `binding` schema.
    Absent when the source has none.
     */
  settings?: {
    [key: string]: any;
  };
  /** The day this tenant has held the site's web addresses from. It
    covers the addresses granted before it is stated: each of those
    is counted from the earlier of this day and the first whole day
    after its grant. An address granted later counts from its own
    grant. The binding's own day sent again states nothing, unless
    `restateHistory` is set; a new or changed day is stated. Without
    it, every address counts from the first whole day after its
    grant. Recorded in the tenant's log when it is stated.
     */
  historyFrom?: string;
  /** The `revision` of the binding this bind replaces, exactly as read.
    Absent only for a site with no binding. Any other binding is a 409.
     */
  expectedRevision?: string;
  /** States `historyFrom` again even when it is the binding's own
    day, so it covers the addresses granted before now. Needs
    `historyFrom`.
     */
  restateHistory?: boolean;
};
export type Agreement = {
  planId: string;
  planName: string;
  interval: Interval;
  amount: Cents;
  paymentTermsDays: number;
  /** The day periods are counted from. */
  anchor: string;
  effectiveFrom: string;
  /** The first day no longer charged. */
  endsOn?: string;
  createdAt: string;
  createdBy: Actor;
};
export type LineKind = "charge" | "adjustment" | "payment";
export type LineStatus = "paid" | "part-paid" | "due" | "overdue";
export type Void = {
  at: string;
  by: Actor;
  reason: string;
};
export type StatementLine = {
  kind: LineKind;
  date: string;
  /** True for an amount owed, false for an amount paid or credited. */
  debit: boolean;
  amount: Cents;
  /** The running balance after this line. A void line does not move it. */
  balance: Cents;
  status?: LineStatus;
  due?: string;
  outstanding?: Cents;
  /** A charge's last day. */
  periodEnd?: string;
  planName?: string;
  interval?: Interval;
  invoiceRef?: string;
  note?: string;
  /** A payment's or adjustment's id. */
  id?: string;
  method?: string;
  reference?: string;
  reason?: string;
  void?: Void;
};
export type UpcomingCharge = {
  periodStart: string;
  periodEnd: string;
  due: string;
  amount: Cents;
  interval: Interval;
  planName: string;
};
export type Billing = {
  asOf: string;
  current?: Agreement;
  /** The earliest day a change to the current agreement may take effect. */
  nextBoundary?: string;
  billed: Billed;
  /** Every agreement, oldest first. */
  agreements: Agreement[];
  /** The statement, oldest first. */
  lines: StatementLine[];
  /** What is owed today. Negative is in credit. */
  balance: Cents;
  overdue: Cents;
  oldestOverdue?: string;
  next?: UpcomingCharge;
  /** The payment methods `recordPayment` accepts. */
  methods: string[];
};
export type SetBilled = {
  billed: Billed;
};
export type RecordPayment = {
  amount: Cents;
  receivedOn: string;
  /** One of the account's `methods`. */
  method: string;
  /** What the bank statement shows. */
  reference?: string;
  note?: string;
};
export type VoidEntry = {
  reason: string;
};
export type AddAdjustment = {
  kind: "charge" | "credit";
  amount: Cents;
  on: string;
  reason: string;
};
export type PeriodNote = {
  invoiceRef?: string;
  text?: string;
};
export type Plan = {
  id: string;
  name: string;
  monthly: Cents;
  annual: Cents;
  /** Whether the plan is offered for new agreements. */
  active: boolean;
};
export type Plans = {
  plans: Plan[];
};
export type PlanInput = {
  name: string;
  monthly: Cents;
  annual: Cents;
  active?: boolean;
};
export type TrafficMethod = "browser" | "server" | "pushed";
export type TrafficSource = {
  /** What a connection of this source declares. */
  kind: string;
  label: string;
  /** A JSON Schema for a binding's `settings`. Absent when a binding
    states none.
     */
  binding?: {
    [key: string]: any;
  };
  /** The source's counts are estimates scaled up from a sample. */
  sampled: boolean;
  method: TrafficMethod;
};
export type TrafficSources = {
  sources: TrafficSource[];
};
export type TrafficConnection = {
  /** What a binding names. */
  ref: string;
  /** `platform` for a connection the deployment declares. */
  owner: string;
  /** The source it is answered by, as `listTrafficSources` lists it. */
  kind: string;
  label: string;
  /** The connection's settings, which are never secret. */
  settings: {
    [key: string]: any;
  };
  /** A token was found the last time the connection was checked.
    Absent until it has been checked.
     */
  configured?: boolean;
  /** When it was last checked. Absent until it has been. */
  checkedAt?: string;
  /** The secret its token is pasted into. */
  secretName: string;
};
export type TrafficConnections = {
  connections: TrafficConnection[];
};
export type OverdueAccount = {
  tenant: string;
  name: string;
  overdue: Cents;
  balance: Cents;
  oldestOverdue: string;
};
export type UpcomingAccountCharge = {
  tenant: string;
  name: string;
  charge: UpcomingCharge;
};
export type MonthReceived = {
  /** The month, `YYYY-MM`. */
  month: string;
  total: Cents;
  payments: number;
};
export type BillingOverview = {
  asOf: string;
  /** Accounts with anything overdue, oldest first. */
  overdue: OverdueAccount[];
  /** Charges starting in the next 30 days, soonest first. */
  upcoming: UpcomingAccountCharge[];
  /** Payments received in each of the last twelve months, newest first. */
  received: MonthReceived[];
  /** Recurring revenue per month, with annual agreements divided by twelve. */
  monthlyRecurring: Cents;
  monthlyCount: number;
  annualCount: number;
};
export type LedgerLine = {
  tenant: string;
  name: string;
  line: StatementLine;
};
export type Ledger = {
  from: string;
  to: string;
  lines: LedgerLine[];
};
export const {
  useGetOperatorOpenApiyamlQuery,
  useLazyGetOperatorOpenApiyamlQuery,
  useListOperatorTenantsQuery,
  useLazyListOperatorTenantsQuery,
  useCreateTenantMutation,
  useGetOperatorTenantQuery,
  useLazyGetOperatorTenantQuery,
  useUpdateTenantMutation,
  useOffboardTenantMutation,
  useRetryPurgeMutation,
  useReinstateTenantMutation,
  useListAdministratorsQuery,
  useLazyListAdministratorsQuery,
  useAddAdministratorMutation,
  useRemoveAdministratorMutation,
  useResetAdministratorMfaMutation,
  useListDomainsQuery,
  useLazyListDomainsQuery,
  useGrantDomainMutation,
  useRevokeDomainMutation,
  useListTenantTrafficQuery,
  useLazyListTenantTrafficQuery,
  useBindTenantTrafficMutation,
  useUnbindTenantTrafficMutation,
  useCollectTenantTrafficMutation,
  useGetTenantBillingQuery,
  useLazyGetTenantBillingQuery,
  useAddAgreementMutation,
  useSetTenantBilledMutation,
  useRecordPaymentMutation,
  useVoidPaymentMutation,
  useAddAdjustmentMutation,
  useVoidAdjustmentMutation,
  useSetPeriodNoteMutation,
  useListPlansQuery,
  useLazyListPlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useListTrafficSourcesQuery,
  useLazyListTrafficSourcesQuery,
  useListTrafficConnectionsQuery,
  useLazyListTrafficConnectionsQuery,
  useGetBillingOverviewQuery,
  useLazyGetBillingOverviewQuery,
  useGetLedgerQuery,
  useLazyGetLedgerQuery,
} = injectedRtkApi;
